/**
 * The reviews wall's moving columns (OI-129): clone each column, and pause.
 *
 * Each column's track holds one group of cards from the server. A seamless
 * vertical loop needs a second, identical group for the track to slide into;
 * rendering the blocks twice would emit their editor attributes twice (CC-02),
 * so the copy is cloned here with those attributes stripped, as
 * `admira-marquee.js` does for the brand marquee. The motion itself is CSS and
 * only starts once `data-cloned` is set.
 *
 * The pause button is the WCAG 2.2.2 control: hover and focus pause the wall
 * too, but a touch reader has neither.
 */
class AdmiraReviewsWall extends HTMLElement {
  connectedCallback() {
    if (this.dataset.cloned === 'true') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    for (const track of this.querySelectorAll('.reviews-wall__track')) {
      const group = track.querySelector('.reviews-wall__group');
      if (!group) continue;

      const copy = /** @type {HTMLElement} */ (group.cloneNode(true));
      copy.setAttribute('aria-hidden', 'true');
      copy.setAttribute('inert', '');

      for (const node of [copy, ...copy.querySelectorAll('*')]) {
        node.removeAttribute('id');
        for (const { name } of [...node.attributes]) {
          if (name.startsWith('data-shopify')) node.removeAttribute(name);
        }
      }

      track.appendChild(copy);
    }

    this.querySelector('.reviews-wall__pause')?.addEventListener('click', this.#toggle);
    this.dataset.cloned = 'true';
  }

  disconnectedCallback() {
    this.querySelector('.reviews-wall__pause')?.removeEventListener('click', this.#toggle);
  }

  #toggle = () => {
    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('.reviews-wall__pause'));
    const paused = this.dataset.paused !== 'true';

    if (paused) {
      this.dataset.paused = 'true';
    } else {
      delete this.dataset.paused;
    }

    if (!button) return;
    button.setAttribute('aria-pressed', String(paused));
    // An icon button: the glyph follows `aria-pressed` in CSS, the name says
    // what a press will do next.
    const label = paused ? button.dataset.labelPlay : button.dataset.labelPause;
    if (label) button.setAttribute('aria-label', label);
  };
}

if (!customElements.get('admira-reviews-wall')) {
  customElements.define('admira-reviews-wall', AdmiraReviewsWall);
}
